import { FC } from 'react';

import Link from 'next/link';

import { Anchor, Badge, Box, Group, Image, Text } from '@mantine/core';
import { TbArrowUpRight, TbPlayerPlay } from 'react-icons/tb';

import { blogCategories } from '@/../marketing/data/blog-categories';
import { BlogPost } from '@/../marketing/data/blog-posts';

import cx from './BlogTeaser.module.css';

const BlogTeaser: FC<{
    blog: BlogPost;
}> = ({ blog }) => {
    const category = blogCategories[blog.category];
    const href = blog.href ?? `/blog/${blog.slug}`;
    const isExternal = !!blog.href;
    const isYoutube = href.includes('youtube.com');

    return (
        <Box className={cx.card} h="100%">
            <Link href={href} target={isExternal ? '_blank' : undefined} className={cx.imageLink}>
                {blog.image ? (
                    <Image
                        src={typeof blog.image === 'string' ? blog.image : blog.image.src}
                        alt={blog.title}
                        className={cx.image}
                        mb="xl"
                    />
                ) : (
                    <Box className={cx.fallback} mb="xl" data-with-bg="grid">
                        {blog.title}
                    </Box>
                )}
                {blog.isVideo && (
                    <Box className={cx.playBadge}>
                        <TbPlayerPlay size={20} />
                    </Box>
                )}
            </Link>

            <Group gap="6" mb={8}>
                <Badge color="black" radius="xs" size="sm">
                    {category.label}
                </Badge>
                {blog.isVideo && (
                    <Badge variant="outline" color="black" radius="xs" size="sm">
                        Video
                    </Badge>
                )}
            </Group>

            <Anchor fw={700} fz="xl" lh={1.4} component={Link} href={href} target={isExternal ? '_blank' : undefined}>
                {blog.title}
            </Anchor>

            <Text c="dimmed" mt={8} fw={500} fz={14}>
                {blog.date} · {blog.author}
            </Text>

            <Text fz={14} my="md">
                {blog.description}
            </Text>

            <Anchor
                fz={13}
                fw={500}
                className={cx.link}
                mt="auto"
                href={href}
                target={isExternal ? '_blank' : undefined}
            >
                {isYoutube ? 'Watch on YouTube' : 'Read More'} <TbArrowUpRight size={14} />
            </Anchor>
        </Box>
    );
};

export { BlogTeaser };
